import { LinkIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useField } from 'formik';
import * as Yup from 'yup';

import { Button } from 'components/button/Button';
import { Combobox } from 'components/combobox/Combobox';
import { Dialog } from 'components/dialog/Dialog';
import { Formik } from 'components/form/formik';
import { InputWrapper } from 'components/InputWrapper';
import { useTranslation } from 'contexts/translation-context';
import { getDisplayError } from '@utils/get-display-error';
import { displayTrailer, type TrailerOption } from 'src/app/trailer/displayTrailer';
import { TrailerCombobox } from 'src/app/trailer/TrailerCombobox';

import { licensePlateMatches } from '../../auth/truck-app-auth.utils';
import {
  linkTelemetryVehicle,
  useLinkableTrucks,
  type TelemetryLinkableTruck,
  type TelemetryParty,
  type TelemetryVehicle,
} from '../models';
import { labelOf } from '../vehicle-labels';
import { VehicleLinkList } from './VehicleLinkList';

/** Which end of a link a picker is choosing. */
type LinkMode = 'truck' | 'trailer';

interface ITruckComboboxFieldProps {
  name: string;
  party?: TelemetryParty;
  /** The plate the provider reported, so the likeliest truck can be shown first. */
  vehiclePlate: string;
}

/** The picker's own field, so the form submits a single value. */
const TruckComboboxField = ({ name, party, vehiclePlate }: ITruckComboboxFieldProps) => {
  const { i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const { data, isLoading } = useLinkableTrucks(party, search);
  const [field, meta, helpers] = useField({ name });

  // The truck whose plate the provider reported is the one the user most likely means, so it leads —
  // and says why, since the plate on either side may be written differently.
  const trucks = useMemo(
    () =>
      [...(data?.trucks ?? [])].sort(
        (a, b) =>
          Number(licensePlateMatches(b.licensePlate, vehiclePlate)) -
          Number(licensePlateMatches(a.licensePlate, vehiclePlate)),
      ),
    [data, vehiclePlate],
  );

  const display = (truck: TelemetryLinkableTruck) => {
    const label = [truck.licensePlate, truck.name].filter(Boolean).join(' · ');
    return licensePlateMatches(truck.licensePlate, vehiclePlate)
      ? `${label} (${i18n('liveTracking.plateMatch')})`
      : label;
  };

  // The cap is worth saying out loud: the list is capped, so a truck missing from it may simply be past
  // the cap — the search box, not the picker's length, is what makes a large fleet reachable.
  const helperText = data?.hasMore
    ? `${i18n('liveTracking.chooseTruckHelp')} ${i18n('liveTracking.moreTrucks', { count: String(data.trucks.length) })}`
    : i18n('liveTracking.chooseTruckHelp');

  return (
    <InputWrapper
      labelText={i18n('liveTracking.chooseTruck')}
      invalidText={meta.touched ? meta.error : undefined}
      helperText={helperText}
    >
      <Combobox
        keyName="id"
        items={trucks}
        selectedItem={field.value ?? null}
        onSelect={(value) => helpers.setValue(value)}
        onQueryChange={setSearch}
        display={display}
        isLoading={isLoading}
        isInvalid={Boolean(meta.touched && meta.error)}
        isNullable
        onBlur={() => helpers.setTouched(true)}
      />
    </InputWrapper>
  );
};

/** Our own trailers, searched server-side — the other end a reported unit can be linked to. */
const TrailerComboboxField = ({ name }: { name: string }) => {
  const { i18n } = useTranslation();
  const [field, meta, helpers] = useField({ name });

  return (
    <InputWrapper labelText={i18n('liveTracking.chooseTrailer')} invalidText={meta.touched ? meta.error : undefined}>
      <TrailerCombobox
        value={field.value ?? null}
        onChange={(value) => helpers.setValue(value)}
        onBlur={() => helpers.setTouched(true)}
        isInvalid={Boolean(meta.touched && meta.error)}
      />
    </InputWrapper>
  );
};

interface ILinkTargetFormProps {
  vehicle: TelemetryVehicle;
  party?: TelemetryParty;
  mode: LinkMode;
  /** The trailer the picker starts on, when the caller already knows which one is meant. */
  initialTrailer?: TrailerOption;
  onLinked: () => void | Promise<void>;
  /** Back to the list of vehicles, without linking. */
  onCancel: () => void;
}

interface ILinkTargetFormValues {
  truck: TelemetryLinkableTruck | null;
  trailer: TrailerOption | null;
}

/**
 * The second step: what the chosen vehicle is linked to.
 *
 * The plate is a hint here rather than the decision: `findTruckForPlate` has already linked whatever the
 * plate settled unambiguously, so what is left is exactly what the plate could not decide — a plate no
 * truck in the platform carries, two trucks sharing one, or a provider that reports none at all.
 */
const LinkTargetForm = ({ vehicle, party, mode, initialTrailer, onLinked, onCancel }: ILinkTargetFormProps) => {
  const { i18n } = useTranslation();
  const [error, setError] = useState('');

  const handleSubmit = async (values: ILinkTargetFormValues) => {
    let body: { supplierTruckId: string } | { trailerId: number };

    if (mode === 'truck') {
      if (!values.truck) {
        return;
      }
      body = { supplierTruckId: values.truck.id };
    } else {
      const trailer = values.trailer ?? initialTrailer;
      if (!trailer) {
        return;
      }
      body = { trailerId: trailer.id };
    }

    setError('');
    try {
      await linkTelemetryVehicle(vehicle.id, body, party);
      await onLinked();
    } catch (err) {
      setError(getDisplayError(err));
    }
  };

  return (
    <Formik<ILinkTargetFormValues>
      initialValues={{ truck: null, trailer: initialTrailer ?? null }}
      validationSchema={Yup.object(
        mode === 'truck'
          ? { truck: Yup.mixed().required(i18n('liveTracking.chooseTruckRequired')) }
          : { trailer: Yup.mixed().required(i18n('liveTracking.chooseTrailerRequired')) },
      )}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: submit, isSubmitting }) => (
        <form
          onSubmit={(evt) => {
            evt.preventDefault();
            submit();
          }}
        >
          {mode === 'truck' ? (
            <TruckComboboxField name="truck" party={party} vehiclePlate={vehicle.licensePlateRaw} />
          ) : (
            <TrailerComboboxField name="trailer" />
          )}

          {error && <div className="mt-4 text-sm text-negative-text">{error}</div>}

          <div className="mt-6 flex justify-between">
            <Button onTrigger={onCancel} isDisabled={isSubmitting}>
              {i18n('liveTracking.back')}
            </Button>
            <Button type="submit" color="primary" isLoading={isSubmitting} isDisabled={isSubmitting}>
              {i18n('liveTracking.link')}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

interface ILinkVehiclesDialogProps {
  /** The vehicles this connection reports — the list the dialog links and unlinks. */
  vehicles: TelemetryVehicle[];
  party?: TelemetryParty;
  onLinked: () => void | Promise<void>;
  onUnlinked: (vehicle: TelemetryVehicle) => void | Promise<void>;
  /** What a picked unit is linked to. A truck by default; a trailer for a fleet of trailer trackers. */
  mode?: LinkMode;
  /** The trailer the picker starts on, when the caller already knows it — the trailer page's own button. */
  initialTrailer?: TrailerOption;
}

/**
 * One button for the connection's vehicle links, rather than a pair of buttons on every row: which
 * vehicle is linked is a property of the connection, and a connection reporting twenty vehicles should
 * not put twenty controls in its body.
 *
 * Picking a vehicle leads to what it is linked to — a truck, or a trailer on an account whose units track
 * trailers; a vehicle that is linked already is unlinked from its own row here instead.
 */
export const LinkVehiclesDialog = ({
  vehicles,
  party,
  onLinked,
  onUnlinked,
  mode = 'truck',
  initialTrailer,
}: ILinkVehiclesDialogProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<TelemetryVehicle | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const close = () => {
    setIsOpen(false);
    // Reopening starts at the list, not at whatever was picked last time.
    setSelected(null);
  };

  const handleUnlink = async (vehicle: TelemetryVehicle) => {
    setPendingId(vehicle.id);
    try {
      await onUnlinked(vehicle);
      close();
    } finally {
      setPendingId(null);
    }
  };

  return (
    <>
      <Button
        color="secondary"
        size={8}
        iconLeft={<LinkIcon className="button-icon" />}
        onTrigger={() => setIsOpen(true)}
      >
        {i18n('liveTracking.link')}
      </Button>

      <Dialog
        isOpen={isOpen}
        onClose={close}
        title={
          selected
            ? `${i18n(mode === 'trailer' ? 'liveTracking.linkTrailer' : 'liveTracking.linkTruck')} — ${labelOf(selected)}`
            : i18n('liveTracking.linkVehicles')
        }
        // Dismissable while choosing a vehicle, not while choosing its target: the form has its own Back.
        isDismissable={!selected}
      >
        {selected ? (
          <LinkTargetForm
            // A different vehicle is a different form, so the chosen target cannot carry over.
            key={selected.id}
            vehicle={selected}
            party={party}
            mode={mode}
            initialTrailer={initialTrailer}
            onLinked={async () => {
              await onLinked();
              close();
            }}
            onCancel={() => setSelected(null)}
          />
        ) : (
          <div className="grid gap-4">
            <p className="text-sm text-fg-muted">
              {i18n(mode === 'trailer' ? 'liveTracking.linkTrailerHint' : 'liveTracking.linkVehiclesHint')}
            </p>

            <VehicleLinkList
              vehicles={vehicles}
              pendingId={pendingId}
              onPick={setSelected}
              canUnlink={(vehicle) => Boolean(vehicle.supplierTruckId || vehicle.trailerId)}
              onUnlink={(vehicle) => void handleUnlink(vehicle)}
            />

            <div className="flex justify-end">
              <Button onTrigger={close}>{i18n('cancel')}</Button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};
